import { ProjectCodeSchema, type ProjectId } from '@voll-intelligence/domain';
import type { Dispatch, SetStateAction } from 'react';

import { t } from '@web/i18n';
import {
  AppIcons,
  type AppIconName,
  Button,
  Card,
  IconSelectorGroup,
  toScreamingSnake,
  useGoBack,
  useOrgContext,
  Scaffold,
} from '@web/ui/core';
import { FormTextAreaField, FormTextField } from '@web/ui/core/molecules/form';
import { fireAndForget, twcn } from '@web/ui/core/utils';
import { useProjectAuthz, useProjectEditor } from '@web/ui/projects/hooks';

type FormType = ReturnType<typeof useProjectEditor>['form'];

export function ProjectIdentityPage(): React.JSX.Element | null {
  const { mode, isReady, form, project } = useProjectEditor();
  const { orgSlug } = useOrgContext();
  const onCancel = useGoBack({ to: '/orgs/$orgSlug/overview', params: { orgSlug } });
  // In create mode the org-level `kanban:projects:create` was already
  // enforced by the route guard, so the submit button is always shown.
  // In edit mode, hide submit when the caller cannot update — backend
  // would 403 the PATCH anyway.
  const projectAuthz = useProjectAuthz((project?.id ?? '') as ProjectId);
  const canSave = mode === 'create' || projectAuthz.has('kanban:projects:update');

  if (!isReady) return null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        fireAndForget(form.handleSubmit());
      }}
    >
      <Scaffold
        body={
          <>
            <Preview form={form} />
            <Fields form={form} mode={mode} />
            <Actions form={form} mode={mode} canSave={canSave} onCancel={onCancel} />
          </>
        }
      />
    </form>
  );
}

function Preview({ form }: Readonly<{ form: FormType }>): React.JSX.Element {
  return (
    <form.Subscribe selector={(s) => [s.values.name, s.values.code, s.values.icon] as const}>
      {([name, code, icon]) => (
        <div className="mb-5.5">
          <Card>
            <div className="grid grid-cols-identity-hero items-center gap-3.5 p-4 pr-5">
              <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary-ink">
                <AppIcons name={icon} size={26} />
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                <p
                  className={twcn(
                    'm-0 truncate font-serif text-h-display font-normal leading-display tracking-display-tight',
                    name.trim() ? '' : 'italic text-faint-foreground',
                  )}
                >
                  {name.trim() || t('project.editor.identity.previewPlaceholder')}
                </p>
                {code ? (
                  <span className="inline-flex h-5 w-fit items-center gap-1 rounded-sm bg-muted px-1.5 font-mono text-card-id text-foreground">
                    <AppIcons name="hash" size={10} />
                    {code}
                  </span>
                ) : null}
              </div>
            </div>
          </Card>
        </div>
      )}
    </form.Subscribe>
  );
}

function Fields({
  form,
  mode,
}: Readonly<{ form: FormType; mode: 'create' | 'edit' }>): React.JSX.Element {
  const propagateNameToCode = (next: string): void => {
    if (mode !== 'create') return;
    const codeField = form.getFieldMeta('code');
    if (codeField?.isDirty) return;
    // dontUpdateMeta keeps isDirty/isTouched untouched — otherwise the
    // very first programmatic setFieldValue would mark the code field
    // dirty and freeze subsequent auto-derivation from the name field.
    form.setFieldValue('code', toScreamingSnake(next), { dontUpdateMeta: true });
  };

  const validateName = (value: string): string | undefined =>
    value.trim().length === 0 ? t('project.editor.fields.name.errorRequired') : undefined;

  const validateCode = (value: string): string | undefined => {
    if (mode === 'edit') return undefined;
    if (!value) return t('project.editor.fields.code.errorRequired');
    return ProjectCodeSchema.safeParse(value).success
      ? undefined
      : t('project.editor.fields.code.errorFormat');
  };

  return (
    <>
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => validateName(value),
          onBlur: ({ value }) => validateName(value),
        }}
      >
        {(field) => (
          <FormTextField
            field={field}
            label={t('project.editor.fields.name.label')}
            required
            autoFocus
            autoComplete="off"
            placeholder={t('project.editor.fields.name.placeholder')}
            onAfterChange={propagateNameToCode}
          />
        )}
      </form.Field>

      <form.Field
        name="code"
        validators={{
          onChange: ({ value }) => validateCode(value),
          onBlur: ({ value }) => validateCode(value),
        }}
      >
        {(field) => {
          const isLocked = mode === 'edit';
          return (
            <FormTextField
              field={field}
              label={t('project.editor.fields.code.label')}
              required={!isLocked}
              labelEndAdornment={
                isLocked ? (
                  <span className="inline-flex items-center gap-1 font-mono text-tiny uppercase tracking-eyebrow-narrow text-faint-foreground">
                    <AppIcons name="lock" size={11} />
                    {t('project.editor.fields.code.immutableTag')}
                  </span>
                ) : null
              }
              autoComplete="off"
              spellCheck={false}
              readOnly={isLocked}
              disabled={isLocked}
              placeholder={t('project.editor.fields.code.placeholder')}
              transformValue={toScreamingSnake}
              mono
              hint={
                isLocked
                  ? t('project.editor.fields.code.hintEdit')
                  : t('project.editor.fields.code.hintCreate')
              }
            />
          );
        }}
      </form.Field>

      <form.Field name="description">
        {(field) => (
          <FormTextAreaField
            field={field}
            label={t('project.editor.fields.description.label')}
            placeholder={t('project.editor.fields.description.placeholder')}
          />
        )}
      </form.Field>

      <div className="mb-2 mt-7">
        <h4 className="m-0 text-base font-medium text-foreground">
          {t('project.editor.fields.icon.section')}
        </h4>
        <p className="m-0 text-meta text-muted-foreground">
          {t('project.editor.fields.icon.sectionHint')}
        </p>
      </div>
      <form.Field name="icon">
        {(field) => (
          <Card>
            <div className="p-2.5">
              <IconSelectorGroup
                value={field.state.value}
                onChange={(next) => {
                  (field.handleChange as unknown as Dispatch<SetStateAction<AppIconName>>)(next);
                }}
                ariaLabel={t('project.editor.fields.icon.section')}
              />
            </div>
          </Card>
        )}
      </form.Field>
    </>
  );
}

function Actions({
  form,
  mode,
  canSave,
  onCancel,
}: Readonly<{
  form: FormType;
  mode: 'create' | 'edit';
  canSave: boolean;
  onCancel: () => void;
}>): React.JSX.Element {
  return (
    <div className="mt-8 flex items-center gap-2 border-t border-border pt-4">
      <span className="font-mono text-card-id tracking-eyebrow-narrow text-faint-foreground">
        {t('project.editor.actions.escHint')}
      </span>
      <span className="flex-1" />
      <form.Subscribe selector={(s) => [s.isSubmitting, s.canSubmit] as const}>
        {([isSubmitting, canSubmit]) => (
          <>
            <Button variant="outline" size="lg" onClick={onCancel} disabled={isSubmitting}>
              {t('project.editor.actions.cancel')}
            </Button>
            {canSave ? (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={!canSubmit || isSubmitting}
              >
                <AppIcons name={mode === 'edit' ? 'check' : 'plus'} size={14} />
                <span>
                  {mode === 'edit'
                    ? t('project.editor.actions.save')
                    : t('project.editor.actions.create')}
                </span>
              </Button>
            ) : null}
          </>
        )}
      </form.Subscribe>
    </div>
  );
}
